import { usePrimaryColor } from '../../branding/usePrimaryColor';
import type { RetentionBucket } from '../../types/dashboard';

interface ClientRetentionProps {
  newCount: number;
  returningCount: number;
  buckets: RetentionBucket[];
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

function formatBucketLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

export function ClientRetention({
  newCount,
  returningCount,
  buckets,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: ClientRetentionProps) {
  const primaryColor = usePrimaryColor();
  const total = newCount + returningCount;

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Client retention</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Client retention</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load retention data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && total === 0 && buckets.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-28 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-6 w-full bg-canvas animate-pulse rounded" />
          <div className="flex gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-3 w-20 bg-canvas animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (total === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Client retention</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No client bookings in this period.
          </p>
        </div>
      </div>
    );
  }

  const newPct = total > 0 ? (newCount / total) * 100 : 0;
  const retPct = total > 0 ? (returningCount / total) * 100 : 0;

  // Stacked bar chart for buckets
  const maxBucket = Math.max(...buckets.map((b) => b.newCount + b.returningCount), 1);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Client retention</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 pb-3">
        {/* Summary bar */}
        <div className="flex h-5 rounded overflow-hidden mb-3">
          {returningCount > 0 && (
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${retPct}%`, backgroundColor: primaryColor, opacity: 0.8 }}
              title={`Returning: ${returningCount} (${retPct.toFixed(0)}%)`}
            />
          )}
          {newCount > 0 && (
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${newPct}%`, backgroundColor: '#10b981', opacity: 0.8 }}
              title={`New: ${newCount} (${newPct.toFixed(0)}%)`}
            />
          )}
        </div>

        {/* Counts */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: primaryColor, opacity: 0.8 }}
            />
            <span className="text-[12px] text-ink-2">Returning</span>
            <span className="text-[12px] font-medium num">{returningCount}</span>
            <span className="text-[10.5px] text-ink-4 num">{retPct.toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0 bg-emerald-500 opacity-80" />
            <span className="text-[12px] text-ink-2">New</span>
            <span className="text-[12px] font-medium num">{newCount}</span>
            <span className="text-[10.5px] text-ink-4 num">{newPct.toFixed(0)}%</span>
          </div>
        </div>

        {/* Bucket breakdown (if >1 bucket) */}
        {buckets.length > 1 && (
          <div className="space-y-1.5">
            {buckets.map((b) => {
              const bTotal = b.newCount + b.returningCount;
              const retW = bTotal > 0 ? (b.returningCount / maxBucket) * 100 : 0;
              const newW = bTotal > 0 ? (b.newCount / maxBucket) * 100 : 0;
              return (
                <div key={b.bucket} className="flex items-center gap-2">
                  <span className="text-[10.5px] text-ink-4 w-12 shrink-0">
                    {formatBucketLabel(b.bucket)}
                  </span>
                  <div className="flex-1 h-4 rounded overflow-hidden bg-canvas flex">
                    {b.returningCount > 0 && (
                      <div
                        className="h-full transition-all duration-300"
                        style={{ width: `${retW}%`, backgroundColor: primaryColor, opacity: 0.8 }}
                      />
                    )}
                    {b.newCount > 0 && (
                      <div
                        className="h-full transition-all duration-300"
                        style={{ width: `${newW}%`, backgroundColor: '#10b981', opacity: 0.8 }}
                      />
                    )}
                  </div>
                  <span className="text-[10.5px] num shrink-0 w-6 text-right">
                    {bTotal}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
